/**
 * Enhanced Markdown Renderer with Emoji Support
 *
 * Features:
 * - Discord-style markdown rendering
 * - Standard emoji shortcodes via Twemoji (:heart:, :fire:, :coin:, etc.)
 * - Unicode emoji rendering via Twemoji
 * - Custom emoji support (Twitch emotes without colons)
 * - Safe HTML rendering with sanitization
 */

import {
    CUSTOM_EMOJIS,
    getColonEmojiMap,
    type CustomEmoji,
} from "@/config/customEmojis";
import twemoji from "@twemoji/api";
import emojiData from "emoji-name-map/lib/datasource.json";
import type { Root } from "mdast";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";

// Pre-compute custom emoji lookup maps
const customColonEmojiMap = getColonEmojiMap();

// Build list of no-colon custom emojis (Twitch-style)
// CUSTOM_EMOJIS is a Record<string, CustomEmoji>
const noColonEmojis: CustomEmoji[] = Object.values(CUSTOM_EMOJIS).filter(
    (e: CustomEmoji) => e.matchWithoutColons !== false,
);

// Build regex for no-colon emoji matching (only custom emojis)
const noColonEmojiRegex =
    noColonEmojis.length > 0
        ? new RegExp(
              `\\b(${noColonEmojis
                  .map((e: CustomEmoji) => e.name)
                  .sort((a: string, b: string) => b.length - a.length)
                  .map((name: string) =>
                      name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                  )
                  .join("|")})\\b`,
              "g",
          )
        : null;

// Create a map for quick lookup of no-colon emojis
const noColonEmojiLookup = new Map<string, CustomEmoji>(
    noColonEmojis.map((e: CustomEmoji) => [e.name, e]),
);

interface TextNode {
    type: string;
    value: string;
}

interface HtmlNode {
    type: "html";
    value: string;
}

// Using generic parent type to work with unist-util-visit
interface TreeParent {
    children: Array<TextNode | HtmlNode | unknown>;
}

/**
 * Remark plugin to replace emoji patterns with Twemoji HTML img tags.
 * Handles both custom emojis (Twitch-style) and standard emoji shortcodes.
 */
function remarkCustomEmoji() {
    return (tree: Root) => {
        visit(tree, "text", (node, index, parent) => {
            if (!parent || index === undefined) return;

            const textNode = node as TextNode;
            const treeParent = parent as unknown as TreeParent;
            const value = textNode.value;
            const parts: Array<TextNode | HtmlNode> = [];
            let hasChanges = false;

            // FIRST handle :colon: style emojis (both custom and standard)
            // This needs to run first because no-colon emojis might match words
            // that appear inside colon-style emoji shortcodes
            const colonRegex = /:([a-zA-Z0-9_+-]+):/g;
            let colonMatch;
            let lastColonIndex = 0;
            const colonParts: Array<TextNode | HtmlNode> = [];

            while ((colonMatch = colonRegex.exec(value)) !== null) {
                const emojiName = colonMatch[1].toLowerCase();
                const customEmoji = customColonEmojiMap.get(emojiName);

                // Check if it's a custom emoji first
                if (customEmoji) {
                    hasChanges = true;
                    if (colonMatch.index > lastColonIndex) {
                        colonParts.push({
                            type: "text",
                            value: value.slice(
                                lastColonIndex,
                                colonMatch.index,
                            ),
                        });
                    }
                    colonParts.push({
                        type: "html",
                        value: `<img src="${customEmoji.url}" alt="${customEmoji.name}" title="${customEmoji.name}" class="inline-block h-6 w-6 align-text-bottom" loading="lazy" />`,
                    });
                    lastColonIndex = colonMatch.index + colonMatch[0].length;
                } else {
                    // Try standard emoji from emoji data
                    const unicodeEmoji = (emojiData as Record<string, string>)[
                        emojiName
                    ];
                    if (unicodeEmoji) {
                        // Convert to twemoji image
                        const twemojiHtml = twemoji.parse(unicodeEmoji, {
                            folder: "svg",
                            ext: ".svg",
                            base: "https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/",
                        });
                        hasChanges = true;
                        if (colonMatch.index > lastColonIndex) {
                            colonParts.push({
                                type: "text",
                                value: value.slice(
                                    lastColonIndex,
                                    colonMatch.index,
                                ),
                            });
                        }
                        colonParts.push({
                            type: "html",
                            value: twemojiHtml.replace(
                                /<img /g,
                                '<img class="inline-block h-6 w-6 align-text-bottom" ',
                            ),
                        });
                        lastColonIndex =
                            colonMatch.index + colonMatch[0].length;
                    }
                }
            }

            // Add remaining text after last colon emoji
            if (lastColonIndex < value.length) {
                colonParts.push({
                    type: "text",
                    value: value.slice(lastColonIndex),
                });
            }

            // If no colon emojis were found, start with original text
            const partsToProcess =
                colonParts.length > 0
                    ? colonParts
                    : [{ type: "text", value } as TextNode];

            // THEN handle no-colon emojis (Twitch-style like vander60PRAY)
            // Process each text part from the colon emoji pass
            for (const part of partsToProcess) {
                if (part.type !== "text") {
                    parts.push(part);
                    continue;
                }

                const textValue = (part as TextNode).value;
                if (!noColonEmojiRegex) {
                    parts.push(part);
                    continue;
                }

                const matches: Array<{
                    match: string;
                    index: number;
                    emoji: CustomEmoji;
                }> = [];
                let match;
                const regex = new RegExp(noColonEmojiRegex.source, "g");
                while ((match = regex.exec(textValue)) !== null) {
                    const emoji = noColonEmojiLookup.get(match[1]);
                    if (emoji) {
                        matches.push({
                            match: match[0],
                            index: match.index,
                            emoji,
                        });
                    }
                }

                if (matches.length === 0) {
                    parts.push(part);
                    continue;
                }

                hasChanges = true;
                let lastIndex = 0;
                for (const { match: m, index: idx, emoji } of matches) {
                    if (idx > lastIndex) {
                        parts.push({
                            type: "text",
                            value: textValue.slice(lastIndex, idx),
                        });
                    }
                    parts.push({
                        type: "html",
                        value: `<img src="${emoji.url}" alt="${emoji.name}" title="${emoji.name}" class="inline-block h-6 w-6 align-text-bottom" loading="lazy" />`,
                    });
                    lastIndex = idx + m.length;
                }
                if (lastIndex < textValue.length) {
                    parts.push({
                        type: "text",
                        value: textValue.slice(lastIndex),
                    });
                }
            }

            if (hasChanges && parts.length > 0) {
                // Replace the current node with multiple nodes
                treeParent.children.splice(index, 1, ...parts);
                return index + parts.length;
            }
        });
    };
}

// Custom sanitize schema to allow images, videos, and emoji img classes
const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [
        ...(defaultSchema.tagNames || []),
        "img",
        "video",
        "iframe",
        "source",
    ],
    attributes: {
        ...defaultSchema.attributes,
        img: [
            "src",
            "alt",
            "title",
            "width",
            "height",
            "class",
            "className",
            "loading",
        ],
        video: [
            "src",
            "controls",
            "width",
            "height",
            "autoplay",
            "loop",
            "muted",
        ],
        iframe: ["src", "width", "height", "frameborder", "allowfullscreen"],
        source: ["src", "type"],
    },
};

interface DiscordMarkdownProps {
    children: string;
    className?: string;
}

/**
 * Discord-style Markdown renderer with emoji support.
 *
 * Features:
 * - GitHub Flavored Markdown (GFM)
 * - Standard emoji shortcodes via Twemoji (:emoji:)
 * - Custom emojis (Twitch-style without colons + :colon: style)
 * - Images and videos
 * - Sanitized HTML output
 */
export function DiscordMarkdown({ children, className }: DiscordMarkdownProps) {
    const remarkPlugins = useMemo(() => [remarkGfm, remarkCustomEmoji], []);

    return (
        <div className={className}>
            <ReactMarkdown
                remarkPlugins={remarkPlugins as never}
                rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
                components={{
                    img: ({ node, className, ...props }) => {
                        // Check if this is an emoji image (from our custom emoji inject)
                        const isEmoji =
                            className?.includes("inline-block") ||
                            props.src?.includes("/emojis/");
                        if (isEmoji) {
                            // Preserve emoji styling
                            return (
                                <img
                                    {...props}
                                    className={
                                        className ||
                                        "inline-block h-6 w-6 align-text-bottom"
                                    }
                                    loading="lazy"
                                />
                            );
                        }
                        // Regular image styling
                        return (
                            <img
                                {...props}
                                className="max-w-full rounded-lg"
                                loading="lazy"
                            />
                        );
                    },
                    video: ({ node, ...props }) => (
                        <video
                            {...props}
                            className="max-w-full rounded-lg"
                            controls
                        />
                    ),
                    a: ({ node, ...props }) => (
                        <a
                            {...props}
                            className="text-blue-400 underline hover:text-blue-300"
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    ),
                    // Make bold text more visible (inherit color from parent)
                    strong: ({ node, ...props }) => (
                        <strong {...props} className="font-bold" />
                    ),
                    // Make emphasis visible
                    em: ({ node, ...props }) => (
                        <em {...props} className="italic" />
                    ),
                    code: ({ node, className, children, ...props }) => {
                        // Inline code doesn't have className from syntax highlighter
                        const isCodeBlock = className?.includes("language-");
                        if (!isCodeBlock) {
                            return (
                                <code
                                    className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-sm text-pink-300"
                                    {...props}
                                >
                                    {children}
                                </code>
                            );
                        }
                        return (
                            <code
                                className={`block overflow-x-auto rounded-lg bg-slate-800 p-4 font-mono text-sm ${className || ""}`}
                                {...props}
                            >
                                {children}
                            </code>
                        );
                    },
                    blockquote: ({ node, ...props }) => (
                        <blockquote
                            className="border-l-4 border-slate-500 pl-4 text-slate-300 italic"
                            {...props}
                        />
                    ),
                }}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
}

/**
 * Simple text renderer with just emoji support (no markdown).
 * Use this for single-line text or when markdown parsing is not needed.
 */
export function TextWithEmojiSupport({
    children,
    className,
}: {
    children: string;
    className?: string;
}) {
    // For simple text without markdown, just handle custom emojis
    const processed = useMemo(() => {
        let text = children;

        // Replace no-colon emojis
        if (noColonEmojiRegex) {
            text = text.replace(noColonEmojiRegex, (match) => {
                const emoji = noColonEmojiLookup.get(match);
                return emoji
                    ? `<img src="${emoji.url}" alt="${emoji.name}" class="inline-block h-6 w-6 align-text-bottom" />`
                    : match;
            });
        }

        return text;
    }, [children]);

    // If there are HTML img tags, use dangerouslySetInnerHTML
    if (processed.includes("<img")) {
        return (
            <span
                className={className}
                dangerouslySetInnerHTML={{ __html: processed }}
            />
        );
    }

    return <span className={className}>{processed}</span>;
}

export default DiscordMarkdown;
